/**
 * A stable, pronounceable identity for an adapter address.
 *
 * The live tape shows other people's trades, and a raw `0x443f…c568` is unusable
 * as a name: you cannot hold it in your head between rows, so a busy feed reads
 * as one anonymous blur. A derived name gives each trader continuity — "Amber
 * Lynx is up again".
 *
 * It is a label, not anonymity. The adapter is public on Hyperliquid, and the
 * chain links it to the wallet that created it, so anyone who cares to look can
 * find the trader behind a row. The name adds nothing to that and hides nothing
 * from it; the tape's own disclosure is decided server-side (what is broadcast,
 * and when), never here.
 *
 * Everything here is a pure function of the address:
 *   - no lookup, no network, no storage, nothing to keep in sync;
 *   - the same address is the same name in every session and every browser.
 *
 * Names are NOT unique. Collisions are expected and harmless because the short
 * address is always rendered beside the name — the name is for recognition, the
 * address is the identity.
 */

/** Adjectives and nouns chosen to stay legible at 10px and to avoid reading as
 *  a real handle. Counts are coprime-ish with the hash mixing so pairs spread. */
const ADJECTIVES = [
    'Amber',
    'Brisk',
    'Calm',
    'Dizzy',
    'Eager',
    'Fuzzy',
    'Glossy',
    'Hazy',
    'Idle',
    'Jolly',
    'Keen',
    'Lucky',
    'Mellow',
    'Nimble',
    'Odd',
    'Plucky',
    'Quiet',
    'Rapid',
    'Silky',
    'Tidy',
    'Upbeat',
    'Vivid',
    'Wry',
    'Zesty',
    'Bold',
    'Crisp',
    'Dusty',
    'Electric',
    'Feral',
    'Grave',
    'Humble',
    'Iron',
] as const;

const NOUNS = [
    'Lynx',
    'Otter',
    'Falcon',
    'Bison',
    'Heron',
    'Marlin',
    'Ocelot',
    'Puffin',
    'Raven',
    'Shark',
    'Tapir',
    'Urchin',
    'Viper',
    'Walrus',
    'Yak',
    'Zebu',
    'Badger',
    'Crane',
    'Dingo',
    'Egret',
    'Ferret',
    'Gecko',
    'Hare',
    'Ibis',
    'Jackal',
    'Kite',
    'Lemur',
    'Mantis',
    'Newt',
    'Osprey',
    'Panther',
    'Quail',
] as const;

/** One emoji per trader, so a row is recognisable before it is read. */
const EMOJI = [
    '🦊',
    '🐙',
    '🦅',
    '🐺',
    '🦉',
    '🐳',
    '🦈',
    '🐝',
    '🦂',
    '🦎',
    '🐢',
    '🦩',
    '🐊',
    '🦡',
    '🦔',
    '🐿️',
    '🦫',
    '🦦',
    '🦥',
    '🐆',
    '🐅',
    '🦓',
    '🦌',
    '🐗',
    '🦇',
    '🦋',
    '🐞',
    '🦗',
    '🕷️',
    '🦑',
    '🦞',
    '🦀',
] as const;

export interface Pseudonym {
    /** e.g. "Amber Lynx" */
    name: string;
    emoji: string;
    /** e.g. "0x443f…c568" — always shown beside the name; this is the identity */
    short: string;
}

/**
 * FNV-1a, 32-bit. Chosen for being tiny and well-mixed at this size — the low
 * bits of an address are not evenly distributed on their own (vanity prefixes,
 * CREATE2 clones sharing structure), so slicing the hex directly would cluster.
 */
function hash32(input: string): number {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        // hash * 16777619, kept in 32-bit range without overflowing to a double
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}

/** `0x443f2f6a…3ac568`, the form every explorer uses. */
export function shortenAddress(address: string, lead = 6, tail = 4): string {
    if (address.length <= lead + tail) return address;
    return `${address.slice(0, lead)}…${address.slice(-tail)}`;
}

/**
 * The pseudonym for an adapter address. Case-insensitive: the same wallet is the
 * same trader whether the backend sent it checksummed or lowercased.
 */
export function pseudonymFor(address: string): Pseudonym {
    const normalized = address.trim().toLowerCase();
    const seed = hash32(normalized);

    // three independent slices of one hash, so the adjective, noun and emoji do
    // not move together between neighbouring addresses
    const adjective = ADJECTIVES[seed % ADJECTIVES.length];
    const noun = NOUNS[(seed >>> 8) % NOUNS.length];
    const emoji = EMOJI[(seed >>> 16) % EMOJI.length];

    return {
        name: `${adjective} ${noun}`,
        emoji,
        short: shortenAddress(address),
    };
}

/** Where a viewer goes to check a row against the exchange itself. */
export function hyperliquidExplorerUrl(address: string): string {
    return `https://app.hyperliquid.xyz/explorer/address/${address}`;
}
